/**
 * Clipboard helpers (ops).
 *
 * Employee feedback #1 (Isaac): clicking an ASIN should copy it. This is the
 * shared copy primitive behind <AsinCopyCell/> (and reusable anywhere else we
 * want click-to-copy) — it prefers the async Clipboard API and falls back to a
 * hidden-textarea + execCommand('copy') on browsers/contexts where
 * navigator.clipboard is unavailable (older Safari, non-secure origins), the
 * same pattern EntityTable's "Copy as TSV" already uses.
 *
 * `normalizeCopyText` is the pure, unit-tested core: it decides what actually
 * gets written (trimmed string, or null when there's nothing meaningful to
 * copy) so the DOM-touching `copyText` stays a thin, untested shell.
 */

/**
 * Coerce an arbitrary cell value into the exact text we'd put on the clipboard,
 * or null when there is nothing worth copying (empty / whitespace-only / the
 * em-dash placeholder we render for missing values). Pure — safe to unit-test.
 */
export function normalizeCopyText(value: unknown): string | null {
  if (value == null) return null
  const s = String(value).trim()
  if (s === '' || s === '—' || s === '-') return null
  return s
}

/**
 * Copy `text` to the clipboard. Returns true on success. No-op returning false
 * when there is nothing meaningful to copy. Prefers the async Clipboard API and
 * degrades to a hidden-textarea execCommand fallback.
 */
export async function copyText(value: unknown): Promise<boolean> {
  const text = normalizeCopyText(value)
  if (text == null) return false
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the legacy path below
  }
  return legacyCopy(text)
}

/** execCommand('copy') fallback for contexts without the async Clipboard API. */
function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false
  const ta = document.createElement('textarea')
  ta.value = text
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  let ok: boolean
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(ta)
  return ok
}
